<template>
    <div
      class="TodoDetails"
    >
        <PopupErrorDisplay/>

        <!-- Skeleton for initial data load -->
        <PatientSidebarSkeleton v-if="initialLoading" />

        <!-- Loading Overlay for action operations (sending forms/texts/emails) -->
        <CommonOverlay
            :show="loading2 && !initialLoading"
            :title="overlayTitle"
            :message="overlayMessage"
        />

        <section v-if="currentStep === 'step1' && sidebarType === 'Form'" v-show="!initialLoading">
            <b-alert
            show
            fade
            variant="success"
            class="m-3"
            >
                <h4 class="alert-heading">Pick one of the two options below</h4>
                <p>You can either select individual forms to send to a patient or you can select a pre-defined Wiki Template. Once you click next,
                    you will be able to edit the message that gets sent out
                </p>
            </b-alert>

            <div>
                <div class="button-panel">
                    <b-row v-if="forms && forms.length > 0">
                        <b-col>
                            <Widget>
                                <div>
                                    <span class="fw-semi-bold">
                                        Select one or more forms below
                                    </span>

                                    <div class="table-resposive">
                                        <v-client-table :data="forms" :columns="columns" :options="options">
                                            <div slot="h__FormIDForWeb"></div>
                                            <div slot="h__FormType">Type</div>

                                            <div slot="FormIDForWeb" slot-scope="props">
                                                <b-form-group class="abc-checkbox mb-1">
                                                    <input
                                                        type="checkbox"
                                                        :id="`form-checkbox${props.row.FormIDForWeb}`"
                                                        class="custom-control-input"
                                                        @click="updateSelectedForms(props.row)"
                                                        :checked="isSelectedForm(props.row.FormIDForWeb)"
                                                    />
                                                    <label :for="`form-checkbox${props.row.FormIDForWeb}`"></label>
                                                </b-form-group>
                                            </div>

                                            <div slot="Name" slot-scope="props">
                                                <span v-if="props.row._isPacket" class="packet-badge">PACKET</span>
                                                {{props.row.Name}}
                                            </div>

                                            <div slot="Type" slot-scope="props">
                                                {{props.row.FormType}}
                                            </div>
                                        </v-client-table>
                                    </div>
                                    <b-button variant="success" type="submit" class="btn-sm mb-2" @click="gotoStep2('forms')">Next</b-button>
                                    <b-button variant="success" type="submit" class="btn-sm mb-2 ml-2" @click="closeSlideOutPanel">Cancel</b-button>
                                </div>
                            </Widget>
                        </b-col>
                    </b-row>

                    <b-row v-if="wikiPages && wikiPages.length > 0">
                        <b-col>
                            <Widget>
                                <div>
                                    <span class="fw-semi-bold">
                                        Select a Wiki template to send to a patient
                                    </span>

                                    <div class="table-resposive">
                                        <v-client-table :data="wikiPages" :columns="wikiColumns" :options="options">
                                            <div slot="h__WikiPageID"></div>
                                            <div slot="WikiPageID" slot-scope="props">
                                                <b-form-group class="abc-checkbox mb-1">
                                                    <input
                                                        type="checkbox"
                                                        :id="`form-checkbox${props.row.WikiPageID}`"
                                                        class="custom-control-input"
                                                        @click="updateSelectedWiki(props.row)"
                                                        :checked="isSelectedWiki(props.row.WikiPageID)"
                                                    />
                                                    <label :for="`form-checkbox${props.row.WikiPageID}`"></label>
                                                </b-form-group>
                                            </div>
                                        </v-client-table>
                                    </div>
                                    <b-button variant="success" type="submit" class="btn-sm mb-2" @click="gotoStep2('wiki')">Next</b-button>
                                    <b-button variant="success" type="submit" class="btn-sm mb-2 ml-2" @click="closeSlideOutPanel">Cancel</b-button>
                                </div>
                            </Widget>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </section>

        <!-- Form Step #2 if they selected forms -->
        <section v-if="currentStep === 'formStep2FromForms'">
            <Widget>
                <div>
                    <h5 class="fw-semi-bold mb-3">How would you like to send these forms?</h5>

                    <!-- Show warning if no delivery methods are available -->
                    <b-alert v-if="!canSendSMS && !canSendEmail" variant="warning" show>
                        <h6 class="alert-heading">No Delivery Methods Available</h6>
                        <p v-if="!smsFeatureEnabled && !emailFeatureEnabled">
                            SMS and Email features are not configured for your account.
                        </p>
                        <p v-else-if="!smsFeatureEnabled">
                            SMS feature is not configured for your account.
                            {{ !canSendEmail ? 'Additionally, this patient does not have an email address.' : '' }}
                        </p>
                        <p v-else-if="smsOutboundOff" data-test="sms-outbound-off-reason">
                            {{ smsDisabledMessage }}
                            {{ !emailFeatureEnabled ? 'Email is not configured for your account either.' : (!canSendEmail ? 'This patient does not have an email address.' : '') }}
                        </p>
                        <p v-else-if="!emailFeatureEnabled">
                            Email feature is not configured for your account.
                            {{ !canSendSMS ? 'Additionally, this patient does not have a valid phone number.' : '' }}
                        </p>
                        <p v-else>
                            This patient does not have valid contact information for SMS or Email.
                        </p>
                        <p class="mb-0">Please contact TeamIO support to enable these features or update the patient's contact information in Open Dental.</p>
                    </b-alert>

                    <!-- Delivery Method Selection -->
                    <b-form-group v-if="canSendSMS || canSendEmail">
                        <b-form-radio-group
                            v-model="deliveryMethod"
                            :options="deliveryMethodOptions"
                            buttons
                            button-variant="outline-primary"
                            size="md"
                            name="delivery-method-radios"
                            class="mb-3"
                        ></b-form-radio-group>
                    </b-form-group>

                    <!-- SMS Option -->
                    <div v-if="deliveryMethod === 'sms' && canSendSMS">
                        <span class="fw-semi-bold">SMS Message:</span>
                        <textarea
                            v-model="smsMessage"
                            rows="6"
                            max-rows="10"
                            cols="10"
                            name
                            placeholder="Write your SMS Message here..."
                            class="form-control form-control-sm"
                        ></textarea>
                        <!-- Shared SMS guidance: live credit counter + warn/block banner + tips -->
                        <SmsGuidance :text="smsMessage || ''" substitute-tags @change="onSmsGuidance" />
                        <div class="d-flex mt-3">
                            <b-button variant="success" type="submit" class="btn-sm" :disabled="smsBlocked" @click="sendText">Send</b-button>
                            <b-button variant="outline-secondary" type="submit" class="btn-sm ml-2" @click="currentStep='step1'">Go Back</b-button>
                            <b-button variant="outline-danger" type="submit" class="btn-sm ml-2" @click="closeSlideOutPanel">Cancel</b-button>
                        </div>
                    </div>

                    <!-- Email Option -->
                    <div v-if="deliveryMethod === 'email' && canSendEmail">
                        <span class="fw-semi-bold">Email Message:</span>
                        <textarea
                            v-model="emailMessage"
                            rows="6"
                            max-rows="10"
                            cols="10"
                            name
                            placeholder="Write your Email Message here..."
                            class="form-control form-control-sm"
                        ></textarea>
                        <div v-if="sendFormEmailLinks.length" class="email-note email-note--links"><i class="fa fa-link mr-1"></i> Items in <b>[Form: ...]</b> become clickable links for this patient when sent.</div>
                        <div class="d-flex mt-3">
                            <b-button variant="success" type="submit" class="btn-sm" @click="sendCustomEmail">Send</b-button>
                            <b-button variant="outline-secondary" type="submit" class="btn-sm ml-2" @click="currentStep='step1'">Go Back</b-button>
                            <b-button variant="outline-danger" type="submit" class="btn-sm ml-2" @click="closeSlideOutPanel">Cancel</b-button>
                        </div>
                    </div>

                    <!-- Show buttons to go back if no delivery methods available -->
                    <div v-if="!canSendSMS && !canSendEmail" class="d-flex mt-3">
                        <b-button variant="outline-secondary" type="submit" class="btn-sm" @click="currentStep='step1'">Go Back</b-button>
                        <b-button variant="outline-danger" type="submit" class="btn-sm ml-2" @click="closeSlideOutPanel">Cancel</b-button>
                    </div>
                </div>
            </Widget>
        </section>

        <!-- Form Step #2 if they selected wiki -->
        <section v-if="currentStep === 'formStep2FromWiki'">
            <Widget>
                <div>
                    <p>You have selected the following WikiPage to be sent</p>
                    <ul mt-3>
                        <li v-for="wiki in selectedWiki" :key="wiki.WikiPageID">
                            {{ wiki.Name }}
                        </li>
                    </ul>
                    <!-- Shared SMS guidance: this step shows no composed text, so show the template's
                         credit cost here — information only, the Send button never waits on it -->
                    <SmsGuidance :text="selectedWikiSmsText" substitute-tags />
                    <div class="d-flex mt-3">
                        <b-button variant="success" type="submit" class="btn-sm" @click="sendTextWithWiki">Send</b-button>
                        <b-button variant="outline-secondary" type="submit" class="btn-sm ml-2" @click="currentStep='step1'">Go Back</b-button>
                        <b-button variant="outline-danger" type="submit" class="btn-sm ml-2" @click="closeSlideOutPanel">Cancel</b-button>
                    </div>
                </div>
            </Widget>
        </section>

        <!-- Text Step #2 -->
        <section v-if="currentStep === 'step1' && sidebarType === 'Text'" v-show="!initialLoading">
            <b-alert
            show
            fade
            variant="success"
            class="m-3"
            >
                <h4 class="alert-heading">Pick one of the two options below</h4>
                <p>You can either type a message to be sent as SMS or you can select a pre-defined Wiki Template</p>
            </b-alert>

            <div v-if="formattedSmsNumber" style="display: flex; align-items: center; gap: 10px; padding: 10px 16px; margin: 0 16px 12px; background: linear-gradient(135deg, #f0fdf4, #dcfce7); border: 1px solid #bbf7d0; border-radius: 8px; font-size: 0.82rem; color: #15803d;">
                <i class="fa fa-phone" style="font-size: 1rem; opacity: 0.7;"></i>
                <span>{{ user && user.SiteName ? user.SiteName : 'Your practice' }}'s outgoing SMS number: <strong style="letter-spacing: 0.3px;">{{ formattedSmsNumber }}</strong></span>
            </div>
            <Widget>
                <div>
                    <span class="fw-semi-bold">SMS Message:</span>
                    <textarea ref="smsTextarea" v-model="smsMessage" @input="onSmsTextareaInput" rows="6" max-rows="10" cols="10" name placeholder="Write your SMS Message here..." class="form-control form-control-sm"></textarea>
                    <div v-if="selectedTemplateHasFormLinks || Object.keys(templateLinkMap).length > 0" class="mt-2" style="font-size: 0.92rem; color: #15803d; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; padding: 10px 12px;">
                        <i class="fa fa-link mr-1"></i> This template includes form links that will be generated as unique patient URLs when sent.
                        <div style="margin-top: 8px; color: #374151; font-size: 0.88rem; line-height: 1.5;">
                            <b>Editing tips:</b> You can freely edit the surrounding text. Items in <b>[Form: ...]</b> represent clickable form links — editing or removing them will exclude the link from the patient's message. Items in <b>curly braces</b> (e.g. patient_first_name) are variables that auto-fill with patient data — you may delete them if not needed, but do not rename them.
                        </div>
                    </div>
                    <div v-if="smsHasVariables" class="mt-2" style="font-size: 0.82rem; color: #6b7280; font-style: italic;">
                        <i class="fa fa-info-circle mr-1"></i> Variables in curly braces will be replaced with actual values when sent.
                    </div>
                    <div v-if="templateTextModified && selectedWiki.length > 0" class="mt-2" style="font-size: 0.82rem; color: #b45309; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 8px 12px;">
                        <i class="fa fa-exclamation-triangle mr-1"></i> You've edited the template text. The message will be sent as-is in English. To send in the patient's preferred language, re-select the template without editing.
                    </div>
                    <!-- Shared SMS guidance: live credit counter + warn/block banner + tips (template-aware) -->
                    <SmsGuidance :text="smsMessage || ''" substitute-tags @change="onSmsGuidance" />
                    <div class="d-flex mt-3 align-items-center">
                        <b-button variant="success" type="submit" class="btn-sm" :disabled="smsBlocked" @click="sendText">Send</b-button>
                        <b-button variant="outline-danger" type="submit" class="btn-sm ml-2" @click="closeSlideOutPanel">Cancel</b-button>

                        <b-dropdown variant="outline-secondary" size="sm" class="ml-3" right no-caret>
                            <template slot="button-content">
                                <strong>+ Insert</strong> <i class="fa fa-caret-down ml-1"></i>
                            </template>

                            <b-dropdown-header>Variables</b-dropdown-header>
                            <b-dropdown-item @click="insertVariable('patient_first_name')"><i class="fa fa-user mr-2 text-muted"></i>Patient First Name</b-dropdown-item>
                            <b-dropdown-item @click="insertVariable('patient_last_name')"><i class="fa fa-user mr-2 text-muted"></i>Patient Last Name</b-dropdown-item>
                            <b-dropdown-item @click="insertVariable('apt_date')"><i class="fa fa-calendar mr-2 text-muted"></i>Appointment Date</b-dropdown-item>
                            <b-dropdown-item @click="insertVariable('apt_dayofweek')"><i class="fa fa-calendar mr-2 text-muted"></i>Appointment Weekday</b-dropdown-item>
                            <!-- 'employee_name' is deliberately not offered: it still resolves
                                 (permanent legacy alias) but two entries meaning the same thing
                                 is the confusion this split was meant to remove. -->
                            <b-dropdown-item @click="insertVariable('employee_full_name')"><i class="fa fa-id-badge mr-2 text-muted"></i>Employee Full Name</b-dropdown-item>
                            <b-dropdown-item @click="insertVariable('employee_first_name')"><i class="fa fa-id-badge mr-2 text-muted"></i>Employee First Name</b-dropdown-item>
                            <b-dropdown-item @click="insertVariable('recall_due_date')"><i class="fa fa-clock-o mr-2 text-muted"></i>Recall Due Date</b-dropdown-item>
                            <b-dropdown-item @click="insertVariable('site_officephone')"><i class="fa fa-phone mr-2 text-muted"></i>Office Phone</b-dropdown-item>

                            <b-dropdown-divider></b-dropdown-divider>

                            <b-dropdown-header>Emojis</b-dropdown-header>
                            <div class="emoji-inline-grid px-2 pb-2">
                                <div v-for="(emojiGroup, category) in emojiTable" :key="category">
                                    <h6 class="emoji-category-title">{{ category }}</h6>
                                    <div class="emoji-grid">
                                        <span
                                            v-for="(emoji, emojiName) in emojiGroup"
                                            :key="emojiName"
                                            class="emoji-item"
                                            :title="emojiName"
                                            @click="insertEmoji(emoji)"
                                        >{{ emoji }}</span>
                                    </div>
                                </div>
                            </div>
                        </b-dropdown>
                    </div>
                </div>

            </Widget>

            <Widget>
                <div>
                    <span class="fw-semi-bold">
                        Or select a template below to prefill your message
                    </span>

                    <div class="table-resposive sms-template-table">
                        <v-client-table :data="wikiPages" :columns="wikiColumns" :options="options" @row-click="onWikiRowClick">
                            <div slot="h__WikiPageID"></div>
                            <div slot="WikiPageID" slot-scope="props">
                                <b-form-group class="abc-radio mb-1">
                                    <input
                                        type="radio"
                                        name="sms-template-radio"
                                        :id="`form-radio${props.row.WikiPageID}`"
                                        class="custom-control-input"
                                        @click="updateSelectedWiki(props.row)"
                                        :checked="isSelectedWiki(props.row.WikiPageID)"
                                    />
                                    <label :for="`form-radio${props.row.WikiPageID}`"></label>
                                </b-form-group>
                            </div>
                        </v-client-table>
                    </div>
                </div>
            </Widget>
        </section>

        <!-- Email Step 1: compose (mirrors the Text panel) + one template list (Email / Text kinds) -->
        <section v-if="currentStep === 'step1' && sidebarType === 'Email'" v-show="!initialLoading">
            <Widget>
                <div>
                    <!-- Saumil 3A (2026-09-10): a designed EMAIL template is sent by id with its OWN subject (the note
                         below names it), so the Subject box hides while one is selected instead of sitting there
                         editable and ignored. emailSubject is kept, so "Write my own message instead" restores it. -->
                    <template v-if="!selectedEmailTemplate">
                        <label class="fw-semi-bold mb-1" for="patient-email-subject">Subject:</label>
                        <input id="patient-email-subject" v-model="emailSubject" type="text" class="form-control form-control-sm mb-2" placeholder="Email subject" data-test="patient-email-subject" />
                    </template>

                    <div v-if="selectedEmailTemplate" class="email-note email-note--designed" data-test="patient-email-designed-note">
                        <i class="fa fa-envelope mr-1"></i> <strong>{{ selectedEmailTemplate.Name }}</strong> will be sent exactly as designed in the Wiki
                        (subject: "{{ selectedEmailTemplate.EmailSubject || selectedEmailTemplate.Name }}").
                        <b-button variant="link" size="sm" class="p-0 ml-1 align-baseline" data-test="patient-email-clear-template" @click="clearEmailTemplateSelection">Write my own message instead</b-button>
                    </div>
                    <template v-else>
                        <div class="d-flex align-items-baseline">
                            <span class="fw-semi-bold">Email Message:</span>
                            <b-button v-if="selectedWiki.length > 0" variant="link" size="sm" class="p-0 ml-auto" data-test="patient-email-clear-template" @click="clearEmailTemplateSelection">Clear template</b-button>
                        </div>
                        <textarea ref="emailTextarea" v-model="emailMessage" rows="8" cols="10" placeholder="Write your email here..." class="form-control form-control-sm" data-test="patient-email-message"></textarea>
                        <div v-if="Object.keys(templateLinkMap).length > 0" class="email-note email-note--links">
                            <i class="fa fa-link mr-1"></i> Items in <b>[Form: ...]</b> become clickable links for this patient when sent. Items in <b>curly braces</b> are filled in automatically — you may delete them, but do not rename them.
                        </div>
                        <div v-if="emailHasVariables" class="email-hint"><i class="fa fa-info-circle mr-1"></i> Variables in curly braces will be replaced with actual values when sent.</div>
                        <!-- Decision 3A: text templates are emailed in English in v1 (no backend translation on this path). -->
                        <div v-if="selectedWiki.length > 0 && patientPrefersNonEnglish" class="email-note email-note--edited" data-test="patient-email-english-note"><i class="fa fa-exclamation-triangle mr-1"></i> This patient prefers a language other than English. Text templates are emailed in English.</div>
                    </template>

                    <div class="email-hint" data-test="patient-email-credit-hint">
                        <i class="fa fa-envelope-o mr-1"></i> 1 email credit per email.
                        <span v-if="emailCreditsBlocked" class="text-danger ml-1">Your practice is out of email credits.</span>
                    </div>

                    <div class="d-flex mt-3 align-items-center">
                        <b-button variant="success" type="submit" class="btn-sm" :disabled="emailCreditsBlocked" data-test="patient-email-send" @click="selectedEmailTemplate ? sendEmailWithWiki() : sendComposedEmail()">Send</b-button>
                        <b-button variant="outline-danger" type="submit" class="btn-sm ml-2" @click="closeSlideOutPanel">Cancel</b-button>
                        <b-dropdown v-if="!selectedEmailTemplate" variant="outline-secondary" size="sm" class="ml-3" right no-caret>
                            <template slot="button-content"><strong>+ Insert</strong> <i class="fa fa-caret-down ml-1"></i></template>
                            <b-dropdown-header>Variables</b-dropdown-header>
                            <b-dropdown-item v-for="v in emailInsertVariables" :key="v.name" @click="insertEmailVariable(v.name)"><i :class="['fa', v.icon, 'mr-2', 'text-muted']"></i>{{ v.label }}</b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </Widget>

            <Widget>
                <div>
                    <span class="fw-semi-bold">Or select a template below</span>
                    <p class="email-hint mb-1">Email templates are sent as designed. Text templates are copied into the message above so you can edit them before sending.</p>
                    <div class="table-resposive sms-template-table">
                        <v-client-table :data="wikiPages" :columns="emailWikiColumns" :options="options" @row-click="onEmailTemplateRowClick">
                            <div slot="h__WikiPageID"></div>
                            <div slot="WikiPageID" slot-scope="props">
                                <b-form-group class="abc-radio mb-1">
                                    <input type="radio" name="email-template-radio" :id="`email-radio${props.row.WikiPageID}`" class="custom-control-input" @click="selectEmailTemplate(props.row)" :checked="isSelectedWiki(props.row.WikiPageID)" />
                                    <label :for="`email-radio${props.row.WikiPageID}`"></label>
                                </b-form-group>
                            </div>
                            <div slot="Kind" slot-scope="props">
                                <span class="kind-badge" :class="props.row.Kind === 'Email' ? 'kind-badge--email' : 'kind-badge--text'">{{ props.row.Kind }}</span>
                            </div>
                        </v-client-table>
                    </div>
                </div>
            </Widget>
        </section>
        <!-- Email Step 1 END -->

        <section v-if="sidebarType === 'ASAP'" v-show="!initialLoading">
            <Widget>
                <b-form class="form-label-left mt-5">

                    <b-form-group
                        horizontal
                        label="Patient:"
                        label-for="patient"
                        :label-cols="4"
                    >
                        <b-row>
                            <b-col md='8'>
                                {{ patientData.FName }} {{ patientData.LName }}
                            </b-col>
                        </b-row>
                    </b-form-group>

                    <b-form-group
                        horizontal
                        label="Availability:"
                        label-for="availability"
                        :label-cols="4"
                    >
                        <b-row>
                            <b-col md='8'>
                                <b-form-group class="abc-checkbox">
                                    <b-form-checkbox
                                        v-model="asapDays"
                                        value="Mon"
                                        unchecked-value="FALSE"
                                    >
                                        <span class="fw-semi-bold">Mon</span>
                                    </b-form-checkbox>

                                    <b-form-checkbox
                                        v-model="asapDays"
                                        value="Tue"
                                        unchecked-value="FALSE"
                                    >
                                        <span class="fw-semi-bold">Tue</span>
                                    </b-form-checkbox>

                                    <b-form-checkbox
                                        v-model="asapDays"
                                        value="Wed"
                                        unchecked-value="FALSE"
                                    >
                                        <span class="fw-semi-bold">Wed</span>
                                    </b-form-checkbox>

                                    <b-form-checkbox
                                        v-model="asapDays"
                                        value="Thur"
                                        unchecked-value="FALSE"
                                    >
                                        <span class="fw-semi-bold">Thur</span>
                                    </b-form-checkbox>

                                    <b-form-checkbox
                                        v-model="asapDays"
                                        value="Fri"
                                        unchecked-value="FALSE"
                                    >
                                        <span class="fw-semi-bold">Fri</span>
                                    </b-form-checkbox>

                                    <b-form-checkbox
                                        v-model="asapDays"
                                        value="Sat"
                                        unchecked-value="FALSE"
                                    >
                                        <span class="fw-semi-bold">Sat</span>
                                    </b-form-checkbox>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-form-group>

                    <b-form-group
                        horizontal
                        label="Notes:"
                        label-for="notes"
                        :label-cols="4"
                    >
                        <b-row>
                        <b-col md="12">
                            <textarea v-model="asapNotes" rows="6" max-rows="6" cols="10" name placeholder="Try to add few new lines..." class="form-control"></textarea>
                            <p class="mt-2 text-muted small">Above is a text that will be sent to the communication log. You can edit it as you like.</p>
                        </b-col>
                        </b-row>
                    </b-form-group>

                  <b-row align-h="center">
                    <b-col>
                      <center><button type="button" class="btn mb-xs mr-xs btn-success" @click="saveAsapList">Save</button> <button type="button" class="ml-2 btn mb-xs mr-xs btn-success" @click="closeSlideOutPanel">Cancel</button></center>
                    </b-col>
                  </b-row>
                </b-form>
            </Widget>
        </section>
    </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import moment from 'moment-timezone';
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import Widget from '@/components/Widget/Widget';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import CommonOverlay from "@/components/CommonOverlay.vue";
import PatientSidebarSkeleton from './PatientSidebarSkeleton.vue';
import SmsGuidance from '../../../components/SmsGuidance.vue';
import { extractSmsTextFromContent } from '../../../utils/smsTemplateText';
import { resolveFromEmail } from '@/utils/verifiedSender.js';
import { buildPatientEmailHtml, mergeEmailPanelTemplates, emailComposeProblems, formLinkPlaceholders, linkMapFromPlaceholders, appendMissingFormLinks } from '@/utils/emailBody.js';
import { findUnknownVariables, describeUnknownVariables } from '@/utils/templateVariables.js';
import { withoutReviewRequestTemplates } from '@/utils/wikiTags';
import { catalogKey, peekCatalog, loadCatalog } from '@/utils/sendPanelCatalog';
import { spliceAtCursor } from '@/utils/textInsert.js';
import { getSmsGuidance } from '../../../utils/smsGuidance';
import { extractApiError } from '@/utils/apiError';
import { describePhoneProblem } from '@/utils/patientPhone.js';
import { hasSmsNumber, isSmsOutboundDisabled, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability.js';
import { optOutResultFromSendFailure } from '@/utils/textConsent.js';
import { classifyNonDelivery } from '@/utils/messaging';
import mixin from '../../../mixin.js';

const EMOJI_TABLE = {
    'Frequently Used': { thumbs_up: '👍', heart: '❤️', smile: '😄', wave: '👋', pray: '🙏', party: '🎉', star: '⭐', check: '✅', fire: '🔥', clap: '👏' },
    'Smileys': { grinning: '😀', smiley: '😃', smile: '😄', grin: '😁', laughing: '😆', blush: '😊', wink: '😉', heart_eyes: '😍', kissing_heart: '😘', thinking: '🤔', neutral: '😐', relieved: '😌', sunglasses: '😎', cry: '😢', sob: '😭', joy: '😂' },
    'Gestures': { thumbs_up: '👍', thumbs_down: '👎', ok_hand: '👌', wave: '👋', clap: '👏', raised_hands: '🙌', pray: '🙏', muscle: '💪', point_right: '👉', point_left: '👈' },
    'Celebrations': { party: '🎉', confetti: '🎊', balloon: '🎈', gift: '🎁', cake: '🎂', trophy: '🏆', sparkles: '✨', star: '⭐', tada: '🎊', ribbon: '🎀' },
    'Hearts & Symbols': { red_heart: '❤️', orange_heart: '🧡', yellow_heart: '💛', green_heart: '💚', blue_heart: '💙', purple_heart: '💜', check: '✅', x: '❌', exclamation: '❗', question: '❓' }
};

// Email "+ Insert" palette — the SAME eight the Text panel offers. Every one resolves on the email
// path since teamio-utility resolveAdHocEmailVariables (2026-09-10). Do NOT add a variable here
// without adding its resolution there: an unresolved token reaches the patient as literal braces.
// Guarded by cypress/node-tests/patient-email-compose.test.js.
const EMAIL_INSERT_VARIABLES = [
  { name: 'patient_first_name', label: 'Patient First Name', icon: 'fa-user' },
  { name: 'patient_last_name', label: 'Patient Last Name', icon: 'fa-user' },
  { name: 'apt_date', label: 'Appointment Date', icon: 'fa-calendar' },
  { name: 'apt_dayofweek', label: 'Appointment Weekday', icon: 'fa-calendar' },
  { name: 'employee_full_name', label: 'Employee Full Name', icon: 'fa-id-badge' },
  { name: 'employee_first_name', label: 'Employee First Name', icon: 'fa-id-badge' },
  { name: 'recall_due_date', label: 'Recall Due Date', icon: 'fa-clock-o' },
  { name: 'site_officephone', label: 'Office Phone', icon: 'fa-phone' },
];

export default {
    name: 'PatientSidebar',
    components: { DatePicker, Multiselect, Widget, PopupErrorDisplay, CommonOverlay, PatientSidebarSkeleton, SmsGuidance },
    props: {
        patientData: {
            type: Object,
            required: true
        },
        sidebarType: {
            type: String,
            required: true
        },
        asapApptList: {
            type: Array,
            default: () => []
        },
        canSendSMS: {
            type: Boolean,
            default: false
        },
        canSendEmail: {
            type: Boolean,
            default: false
        },
        smsFeatureEnabled: {
            type: Boolean,
            default: false
        },
        emailFeatureEnabled: {
            type: Boolean,
            default: false
        }
    },
    mixins: [mixin],

    created() {
        this.init();
    },

    data() {
        return {
            initialLoading: true,
            currentStep: 'step1',
            forms: [],
            formCategories: [],
            wikiPages: [],
            hiddenReviewTemplates: 0, // review-request pages loadLists() kept out of wikiPages (the redesigned panels say so)
            siteTags: [],
            selectedForms: [],
            sendFormEmailLinks: [], // Send Form email: [{ placeholder, anchor }] per selected form, built in gotoStep2('forms') and reused by sendCustomEmail
            selectedWiki: [],
            selectedTemplateHasFormLinks: false,
            templateFormLinks: [], // [{name: 'Medical History', formID: 'xxx-yyy'}]
            templateLinkMap: {}, // { '[Form: text]': '<a href="...">text</a>' } for placeholder restoration
            templateTextModified: false,
            smsMessage: null,
            smsBlocked: false, // set by <SmsGuidance> when the composed text is over the carrier limit
            lastGuidance: null, // latest <SmsGuidance> payload, for the send-time cost confirm
            emailMessage: null,
            deliveryMethod: 'sms', // Default to SMS
            overlayTitle: 'Loading Content',
            overlayMessage: 'Please wait while we prepare the form options...',
            columns: ["FormIDForWeb", "Name", "FormType"],
            wikiColumns: ["WikiPageID", "Name", "Tags"],
            emailSubject: '',
            selectedEmailTemplate: null, // an Email-kind row (SendGrid-backed) — sent by id, path unchanged
            emailWikiColumns: ["WikiPageID", "Name", "Kind"],
            emailInsertVariables: EMAIL_INSERT_VARIABLES,
            emojiTable: EMOJI_TABLE,
            asapDays: [],
            asapNotes: '',
            options: {
                filterable: false,
                skin: "table mt-1",
                sortable: [],
                perPage: 5,
                sortByColumn: false,
                filterByColumn: true,
                filterable: ['Name'],
                pagination: { chunk: 5, dropdown: false },
                columnsClasses: {user:'text-center', note: 'note-col-width'}
          },
        }
    },

    methods: {

        ...mapActions('forms', ['getForms', 'getFormUrl', 'getPackets']),
        ...mapActions('wiki', ['getWikiPages']),
        ...mapActions('teamio', ['sendTextMsg', 'sendEmailMsg', 'saveAsap']),

        // Method to select a single wiki template (radio button behavior)
        selectSingleWiki(wikiPage) {
            this.selectedWiki = [wikiPage];
        },



        // Method to send custom email with forms
        async sendCustomEmail() {
            this.resetAllErrors();

            // Check if Email is enabled for the site
            if(!this.emailFeatureEnabled) {
                this.setErrored2(true);
                this.setMessage2("Email feature is not enabled for your account. Please contact TeamIO support.");
                return false;
            }

            // Check if patient can receive email
            if(!this.canSendEmail) {
                this.setErrored2(true);
                this.setMessage2("Cannot send email to this patient. Please verify the patient has a valid email address in Open Dental.");
                return false;
            }

            if(!this.emailMessage || this.emailMessage.trim() === '') {
                this.setErrored2(true);
                this.setMessage2("Please enter an email message before sending");
                return false;
            }

            if(!this.allSettings.GENERAL_VERIFIED_EMAILS || this.allSettings.GENERAL_VERIFIED_EMAILS.PropertyValue === '') {
                this.setErrored2(true);
                this.setMessage2("Your site does not seem to be set up for email sending yet. Please contact teamio support");
                return false;
            }

            if(this.patientData.Email === null || this.patientData.Email.trim() === '') {
                this.setErrored2(true);
                this.setMessage2("Patient email is missing. Please update patient email and try again");
                return false;
            }

            try {
                this.overlayTitle = 'Sending Email';
                this.overlayMessage = 'Please wait while we send your email with forms...';
                this.setLoading2(true);

                // The [Form: …] placeholders the prefill showed (stored by gotoStep2) map back to their
                // anchors here, so each form NAME is the link on its own list line. Fall back to
                // rebuilding from the current selection if the prefill did not run.
                const entries = this.sendFormEmailLinks.length ? this.sendFormEmailLinks : formLinkPlaceholders(this.selectedForms);

                let siteInfo = this.getSite(this.siteID);

                let payload = {
                    siteID: this.siteID,
                    patNum: this.patientData.PatNum,
                    emailFromName: siteInfo.Name,
                    // Parsed via the shared helper — the setting can be a CSV list
                    // (teamio-admin writes one when a second sender is added), and
                    // the previous direct .PropertyValue read also threw outright
                    // on a site with no verified sender row at all.
                    emailFromEmail: resolveFromEmail(this.allSettings.GENERAL_VERIFIED_EMAILS),
                    emailSubject: `Forms from ${siteInfo.Name}`,
                    // Shared builder (utils/emailBody.js) keeps line breaks and escapes typed text;
                    // Send Form deliberately sends NO signature block, because its prefilled text
                    // already signs off and forms emails never carried one — adding it is a separate decision.
                    // Each form's link is written on its own list line via linkMap; a placeholder a staff
                    // member removed is appended at the end so no form link is ever lost.
                    emailHtml: buildPatientEmailHtml({ message: this.emailMessage, linkMap: linkMapFromPlaceholders(entries), appendHtml: appendMissingFormLinks(this.emailMessage, entries), includeSignature: false }),
                    emailToName: this.patientData.FName,
                    emailToEmail: this.patientData.Email,
                    customVars: {
                        eventType: 'forms_sent',
                        eventDescription: '',
                        patNum: this.patientData.PatNum,
                        formIDs: this.selectedForms.map(form => form.FormIDForWeb),
                        employeeID: this.userID
                    }
                };

                console.log('sendCustomEmail payload', payload);
                await this.sendEmailMsg(payload);
                this.closeSlideOutPanel();
                this.setLoaded2(true);
                this.$toast.success('Email with forms queued. It will be sent to the patient shortly.');
            } catch(err) {
                this.setErrored2(true);
                this.setMessage2(err);
            }
        },

        // The lists this popup type shows, ready to assign: { forms, formCategories, wikiPages }. A type
        // leaves out the ones it never reads (Text and Email use neither forms, packets nor categories),
        // and everything a type needs is requested AT ONCE — they used to load one after another.
        // They come through the per-site catalog (utils/sendPanelCatalog.js, see catalogRead): the
        // lists are per SITE, so a fresh copy is shown immediately and refreshed in the background.
        // `mode` is 'refreshed' when called again after a background refresh landed.
        // Failure semantics are unchanged: a failed getForms still ends in the catch of init() (res is
        // undefined, so res.forms throws), a failed packets read yields no packets, a failed wiki read
        // for Email still shows the other half.
        async loadLists(mode) {
            const siteID = this.siteID;
            const read = (kind, payload) => this.catalogRead(kind, payload, mode);
            const lists = {};
            let wikiPagesResults;
            if(this.sidebarType === 'Form') {
                const [res, packetsRes, formWiki] = await Promise.all([
                    // ConfigData is never read by this popup, so it is not downloaded (FormsHome does the same).
                    read('forms', { siteID, includeConfigData: false }),
                    read('packets', { siteID }).then(
                        (r) => { if (r === undefined) console.error('Failed to load packets for PatientSidebar: no response'); return r; },
                        (err) => { console.error('Failed to load packets for PatientSidebar:', err); return []; },
                    ),
                    read('wiki', { siteID, tags: 'form' }),
                ]);
                lists.formCategories = res.categories;

                // Form Packets are merged into the list so the user can send a packet link
                // via the same picker. Packet rows are flagged with `_isPacket: true`; downstream
                // code (render, gotoStep2, sendCustomEmail) branches on that flag.
                // Key trick: set `FormIDForWeb = PacketID` so v-client-table row keys + selection plumbing
                // (`updateSelectedForms`, `isSelectedForm`) keep working with zero changes.
                const packets = Array.isArray(packetsRes) ? packetsRes : [];
                const packetRows = packets.map(p => ({
                    FormIDForWeb: p.PacketID,
                    Name: p.Name,
                    FormType: '',
                    _isPacket: true,
                    PacketID: p.PacketID,
                }));
                lists.forms = [...(res.forms || []), ...packetRows];
                wikiPagesResults = formWiki;
            } else if(this.sidebarType === 'Email') {
                // Both kinds in one list: designed email templates (sent by id) + SMS templates
                // (copied into the composer). Merged/ordered by utils/emailBody.js.
                const [emailRes, smsRes] = await Promise.all([
                    read('wiki', { siteID, tags: 'email-template' }),
                    read('wiki', { siteID, tags: 'sms-template' }),
                ]);
                // getWikiPages resolves undefined on failure (it has already shown its own error), so
                // guard both — one failed query must not blank the panel; show whatever half loaded.
                wikiPagesResults = [mergeEmailPanelTemplates(emailRes && emailRes[0], smsRes && smsRes[0])];
            } else {
                wikiPagesResults = await read('wiki', { siteID, tags: 'sms-template' });
            }

            // Reviews templates stay out: only the Reviews send path can fill their
            // {{review_link}} (Patient Profile has its own "Send review request" button).
            try {
                lists.wikiPages = wikiPagesResults[0]
                    ? withoutReviewRequestTemplates(wikiPagesResults[0])
                    : wikiPagesResults[0];
                lists.hiddenReviewTemplates = wikiPagesResults[0] ? wikiPagesResults[0].length - lists.wikiPages.length : 0;
            } catch (err) {
                // The template read failed (undefined). The forms were already on screen at this point before,
                // so hand them over together with the error: init() applies them, then raises it.
                lists.error = err;
            }
            return lists;
        },

        async init() {
            this.resetAllErrors();
            this.overlayTitle = 'Loading Content';
            this.overlayMessage = 'Please wait while we prepare the form options...';
            this.setLoading2(true);

            try {
                if(this.sidebarType === 'ASAP') {
                    if(this.asapApptList && this.asapApptList.length > 0) {
                        this.asapDays = this.asapApptList[0].AvailableDays ? this.asapApptList[0].AvailableDays.split(',') : [];
                        this.asapNotes = this.asapApptList[0].AsapNotes;
                    }
                } else {
                    const lists = await this.loadLists();
                    this.applyLists(lists);
                    if (lists.error) throw lists.error;
                }
                this.initialLoading = false;
                this.setLoaded2(true);

                // Pre-fill SMS greeting when opening the Text sidebar
                if (this.sidebarType === 'Text') {
                    this.smsMessage = this.smsGreeting;
                    this.$nextTick(() => {
                        if (this.$refs.smsTextarea) {
                            this.$refs.smsTextarea.focus();
                            const len = (this.smsMessage || '').length;
                            this.$refs.smsTextarea.setSelectionRange(len, len);
                        }
                    });
                }

                if (this.sidebarType === 'Email') {
                    const siteName = this.user && this.user.SiteName ? this.user.SiteName : 'your dental office';
                    this.emailSubject = `Message from ${siteName}`;
                    this.emailMessage = this.emailGreeting;
                }
            } catch(err) {
                this.initialLoading = false;
                this.setErrored2(true);
                this.setMessage2(err);
            }
        },

        // One list through the per-site catalog (utils/sendPanelCatalog.js).
        //   - A fresh-enough copy is returned at once, and ONE deduped background refresh is started. The
        //     refresh is silent (the popup is already showing the list); see catalogRefreshed.
        //   - Otherwise it is one request, shared with any concurrent caller (e.g. the profile's prefetch).
        //     It keeps the store actions' own alerts — except packets, which are always read silently:
        //     that is how a FAILED packets read (resolves undefined) is told apart from "no packets", so a
        //     failure is never cached. init() already treats a missing packets list as none.
        //   - mode 'refreshed' (called after a refresh landed) starts no further refresh and loads silently.
        catalogRead(kind, payload, mode) {
            const key = catalogKey(kind, payload);
            const call = (silent) => {
                const p = silent ? { ...payload, silent: true } : payload;
                if (kind === 'forms') return this.getForms(p);
                if (kind === 'packets') return this.getPackets(p);
                return this.getWikiPages(p);
            };
            const hit = peekCatalog(key);
            if (hit) {
                if (mode !== 'refreshed') {
                    loadCatalog(key, () => call(true)).then(
                        (data) => { if (data !== undefined && data !== null) this.catalogRefreshed(); },
                        () => {},
                    );
                }
                return Promise.resolve(hit.data);
            }
            return loadCatalog(key, () => call(mode === 'refreshed' || kind === 'packets'));
        },

        // Assign loadLists() output (its `error`, if any, is init()'s to raise). `onlyIfChanged` (a refresh)
        // leaves an identical list alone, so an unchanged site never re-renders the table or resets its page.
        applyLists(lists, onlyIfChanged) {
            ['forms', 'formCategories', 'wikiPages', 'hiddenReviewTemplates'].forEach((k) => {
                if (!(k in lists)) return;
                if (onlyIfChanged && JSON.stringify(this[k]) === JSON.stringify(lists[k])) return;
                this[k] = lists[k];
            });
        },

        // A background refresh landed. Show the new lists ONLY while nothing is picked: a list that changes
        // under a selection would drop or mis-point the pick. Otherwise leave it — the next open gets it.
        async catalogRefreshed() {
            const untouched = () => !this._isDestroyed && this.selectedForms.length === 0 && this.selectedWiki.length === 0;
            if (!untouched()) return;
            try {
                const lists = await this.loadLists('refreshed');
                if (untouched()) this.applyLists(lists, true);
            } catch (err) {
                console.error('Could not apply the refreshed send-popup lists:', err);
            }
        },

        // ONE insert-at-cursor for both composers (review CQ-2A). REPLACES the bodies of the existing
        // insertVariable / insertEmoji, which were two copies of the same ten lines. The splice itself
        // is pure and tested for real: src/utils/textInsert.js (Tests review 4A).
        insertAtCursor(refName, modelKey, insert) {
            const textarea = this.$refs[refName];
            if (!textarea) return;
            const { text, caret } = spliceAtCursor(this[modelKey], textarea.selectionStart, textarea.selectionEnd, insert);
            this[modelKey] = text;
            this.$nextTick(() => { textarea.setSelectionRange(caret, caret); textarea.focus(); });
        },
        insertVariable(varName) { this.insertAtCursor('smsTextarea', 'smsMessage', `{{${varName}}}`); },
        insertEmoji(emoji) { this.insertAtCursor('smsTextarea', 'smsMessage', emoji); },
        insertEmailVariable(varName) { this.insertAtCursor('emailTextarea', 'emailMessage', `{{${varName}}}`); },
        onEmailTemplateRowClick(event) { this.selectEmailTemplate(event.row); },
        // IDEMPOTENT on purpose: one click on the radio fires the input's @click AND the row's
        // row-click (and a label click fires the input's click again), so a toggle here would select
        // and then un-select on a single click. Selecting the selected row is a no-op; clearing is an
        // explicit control ("Write my own message instead" / "Clear template").
        selectEmailTemplate(row) {
            const already = this.selectedWiki.some(w => w.WikiPageID === row.WikiPageID);
            if (already) return;
            this.selectedWiki = [row];
            if (row.Kind === 'Email') {
                // Designed email template: sent by id through the unchanged SendGrid path.
                this.selectedEmailTemplate = row;
                this.templateLinkMap = {};
                this.selectedTemplateHasFormLinks = false;
            } else {
                // SMS template: same parser as the Text panel — sets templateLinkMap for [Form: …].
                this.selectedEmailTemplate = null;
                this.emailMessage = this.parseEditorJsContent(row.Content);
            }
        },
        clearEmailTemplateSelection() {
            this.selectedWiki = [];
            this.selectedEmailTemplate = null;
            this.templateLinkMap = {};
            this.selectedTemplateHasFormLinks = false;
            this.emailMessage = this.emailGreeting;
        },

        async sendComposedEmail() {
            this.resetAllErrors();
            if(!this.emailFeatureEnabled) {
                this.setErrored2(true);
                this.setMessage2("Email feature is not enabled for your account. Please contact TeamIO support.");
                return false;
            }
            if(!this.canSendEmail) {
                this.setErrored2(true);
                this.setMessage2("Cannot send email to this patient. Please verify the patient has a valid email address in Open Dental.");
                return false;
            }
            // Degenerate states are REFUSED, never sent-and-toasted: blank subject/message, a variable the
            // backend cannot fill (would reach the patient as braces), a zero email balance.
            const unknown = findUnknownVariables(this.emailMessage || '');
            const problems = emailComposeProblems({
                subject: this.emailSubject, message: this.emailMessage, greeting: this.emailGreeting,
                unknownVariables: unknown, unknownVariablesMessage: describeUnknownVariables(unknown),
                walletOn: this.emailWalletOn, creditsAvailable: this.emailCreditsAvailable,
            });
            if (problems.length) { this.setErrored2(true); this.setMessage2(problems.join(' ')); return false; }
            const fromEmail = resolveFromEmail(this.allSettings.GENERAL_VERIFIED_EMAILS);
            if(!fromEmail) {
                this.setErrored2(true);
                this.setMessage2("Your site does not seem to be set up for email sending yet. Please contact teamio support");
                return false;
            }
            try {
                this.overlayTitle = 'Sending Email';
                this.overlayMessage = 'Please wait while we queue your email...';
                this.setLoading2(true);
                const siteInfo = this.getSite(this.siteID);
                const payload = {
                    siteID: this.siteID,
                    patNum: this.patientData.PatNum,
                    emailFromName: siteInfo.Name,
                    emailFromEmail: fromEmail,
                    emailSubject: this.emailSubject.trim(),
                    emailHtml: buildPatientEmailHtml({ message: this.emailMessage, linkMap: this.templateLinkMap, includeSignature: true }),
                    emailToName: this.patientData.FName,
                    emailToEmail: this.patientData.Email,
                    // Who is sending — resolves {{employee_*}} and stamps commlog userNum in teamio-utility.
                    employeeName: this.user && this.user.Name ? this.user.Name : undefined,
                    userODNum: this.user && this.user.ODUserNum ? this.user.ODUserNum : undefined,
                    customVars: {
                        eventType: 'patient_email',
                        employeeID: this.userID,
                        wikiPageID: this.selectedWiki.length ? this.selectedWiki[0].WikiPageID : null,
                    },
                };
                await this.sendEmailMsg(payload);
                this.closeSlideOutPanel();
                this.setLoaded2(true);
                this.$toast.success('Email queued. It will be sent to the patient shortly.');
            } catch(err) {
                console.error('Error sending composed email from PatientSidebar:', err);
                this.setErrored2(true);
                this.setMessage2(extractApiError(err, 'Failed to send email'));
            }
        },

        async sendText() {
            this.resetAllErrors();

            // Check if SMS is enabled for the site
            if(!this.smsFeatureEnabled) {
                this.setErrored2(true);
                this.setMessage2("SMS feature is not enabled for your account. Please contact TeamIO support.");
                return false;
            }

            // WhatsApp-only office: the number exists but outbound SMS is switched off.
            if(this.smsOutboundOff) {
                this.setErrored2(true);
                this.setMessage2(SMS_DISABLED_MESSAGE);
                return false;
            }

            // Check if patient can receive SMS. canSendSMS is the shared predicate from
            // PatientProfile (opt-out + src/utils/patientPhone.js); name the ACTUAL number
            // problem here rather than asking the operator to go verify it themselves.
            if(!this.canSendSMS) {
                this.setErrored2(true);
                this.setMessage2(
                    describePhoneProblem(this.patientData && this.patientData.WirelessPhone)
                    || "Cannot send SMS to this patient. Please verify the patient has a valid phone number in Open Dental."
                );
                return false;
            }

            if(!this.smsMessage || this.smsMessage.trim() === '') {
                this.setErrored2(true);
                this.setMessage2("Please enter a message before sending");
                return false;
            }

            try {
                this.overlayTitle = 'Sending Text';
                this.overlayMessage = 'Please wait while we send your text message...';
                this.setLoading2(true);

                let siteInfo = this.getSite(this.siteID);

                let payload = {
                    siteID: this.siteID,
                    patNum: this.patientData.PatNum,
                    defaultDB: siteInfo.DefaultDB,
                    userODNum: this.user.ODUserNum,
                    // Marks this as a staff send ('manual') in sms_send_log + alerts, not 'unknown'.
                    employeeName: this.user && this.user.Name ? this.user.Name : undefined
                }

                // If a wiki template is selected and user hasn't modified the text,
                // send only the template ID so the backend can fetch the translated version
                if (this.selectedWiki.length > 0 && !this.templateTextModified) {
                    payload.smsTemplateID = this.selectedWiki[0].WikiPageID;
                } else {
                    payload.smsText = this.smsMessage;

                    // Restore [Form: ...] and [Link: ...] placeholders back to HTML anchors
                    // so the backend's processLink() resolves them into patient-specific URLs
                    if (this.templateLinkMap && Object.keys(this.templateLinkMap).length > 0) {
                        let smsText = payload.smsText;
                        for (const [placeholder, html] of Object.entries(this.templateLinkMap)) {
                            smsText = smsText.replace(placeholder, html);
                        }
                        payload.smsText = smsText;
                    }
                }

                console.dir(payload);

                const response = await this.sendTextMsg(payload);
                
                // Expected "not sent" outcomes come back HTTP 200 { success:false, error }
                // (opt-out / undeliverable). Surface them in the panel's error area — never a
                // false "sent" toast. Opt-out via the shared optOutResultFromSendFailure (server
                // reason, else GENERIC_OPT_OUT); any other non-delivery via classifyNonDelivery.
                const sendData = response && response.data;
                const optOut = optOutResultFromSendFailure(sendData);
                if (optOut) { this.setErrored2(true); this.setMessage2(optOut.reason); return false; }
                if (sendData && sendData.success === false) {
                    this.setErrored2(true);
                    this.setMessage2(classifyNonDelivery(sendData).message);
                    return false;
                }

                this.closeSlideOutPanel();
                this.setLoaded2(true);
                this.$toast.success('Text sent successfully!');
            } catch(err) {
                console.error('Error sending text from PatientSidebar:', err);
                this.setErrored2(true);
                
                // Enhanced error handling similar to Conversations
                const errorMessage = extractApiError(err, 'Failed to send text message');
                
                this.setMessage2(errorMessage);
            }
        },

        async sendTextWithWiki() {
            this.resetAllErrors();

            // Check if SMS is enabled for the site
            if(!this.smsFeatureEnabled) {
                this.setErrored2(true);
                this.setMessage2("SMS feature is not enabled for your account. Please contact TeamIO support.");
                return false;
            }

            // WhatsApp-only office: the number exists but outbound SMS is switched off.
            if(this.smsOutboundOff) {
                this.setErrored2(true);
                this.setMessage2(SMS_DISABLED_MESSAGE);
                return false;
            }

            // Check if patient can receive SMS. canSendSMS is the shared predicate from
            // PatientProfile (opt-out + src/utils/patientPhone.js); name the ACTUAL number
            // problem here rather than asking the operator to go verify it themselves.
            if(!this.canSendSMS) {
                this.setErrored2(true);
                this.setMessage2(
                    describePhoneProblem(this.patientData && this.patientData.WirelessPhone)
                    || "Cannot send SMS to this patient. Please verify the patient has a valid phone number in Open Dental."
                );
                return false;
            }

            if(this.selectedWiki.length === 0 || this.selectedWiki.length > 1) {
                this.setErrored2(true);
                this.setMessage2("Please select ONLY one wiki before continuing");
                return false;
            }

            // We send the template by ID, so read its own text for the empty-template gate below.
            // (The credit counter for this step renders from selectedWikiSmsText.)
            const templateText = this.parseEditorJsContent(this.selectedWiki[0].Content);

            // Empty-template gate. THIS is the path that caused the 2026-08-04 PROD outage: it
            // posts smsTemplateID to /send-text-msg without ever inspecting the body, so a
            // template with no text was accepted here, crashed the send worker, and stalled the
            // practice's entire message queue while the operator saw a success toast.
            // (Its sibling sendText() was only ever safe by accident — it validates the composed
            // textarea, which this step doesn't have.)
            if (!templateText || templateText.trim() === '') {
                this.setErrored2(true);
                this.setMessage2("This template has no message text, so there is nothing to send. Open it in the Wiki and add the text first.");
                return false;
            }

            try {
                this.overlayTitle = 'Sending Template';
                this.overlayMessage = 'Please wait while we send your template via text...';
                this.setLoading2(true);

                let siteInfo = this.getSite(this.siteID);

                let payload = {
                    siteID: this.siteID,
                    patNum: this.patientData.PatNum,
                    smsTemplateID: this.selectedWiki[0].WikiPageID,
                    defaultDB: siteInfo.DefaultDB,
                    userODNum: this.user.ODUserNum
                }
                console.dir(payload);
                
                const response = await this.sendTextMsg(payload);
                
                // Expected "not sent" outcomes come back HTTP 200 { success:false, error }
                // (opt-out / undeliverable). Surface them in the panel's error area — never a
                // false "sent" toast. Opt-out via the shared optOutResultFromSendFailure (server
                // reason, else GENERIC_OPT_OUT); any other non-delivery via classifyNonDelivery.
                const sendData = response && response.data;
                const optOut = optOutResultFromSendFailure(sendData);
                if (optOut) { this.setErrored2(true); this.setMessage2(optOut.reason); return false; }
                if (sendData && sendData.success === false) {
                    this.setErrored2(true);
                    this.setMessage2(classifyNonDelivery(sendData).message);
                    return false;
                }

                this.closeSlideOutPanel();
                this.setLoaded2(true);
                this.$toast.success('Text sent successfully!');
            } catch(err) {
                console.error('Error sending text template from PatientSidebar:', err);
                this.setErrored2(true);
                
                // Enhanced error handling similar to Conversations
                const errorMessage = extractApiError(err, 'Failed to send text template');
                
                this.setMessage2(errorMessage);
            }
        },

        async sendEmailWithWiki() {
            this.resetAllErrors();

            // Check if Email is enabled for the site
            if(!this.emailFeatureEnabled) {
                this.setErrored2(true);
                this.setMessage2("Email feature is not enabled for your account. Please contact TeamIO support.");
                return false;
            }

            // Check if patient can receive email
            if(!this.canSendEmail) {
                this.setErrored2(true);
                this.setMessage2("Cannot send email to this patient. Please verify the patient has a valid email address in Open Dental.");
                return false;
            }

            if(this.selectedWiki.length === 0 || this.selectedWiki.length > 1) {
                this.setErrored2(true);
                this.setMessage2("Please select ONLY one wiki before continuing");
                return false;
            }

            try {
                this.overlayTitle = 'Sending Template';
                this.overlayMessage = 'Please wait while we send your template via email...';
                this.setLoading2(true);

                let payload = {
                    siteID: this.siteID,
                    patNum: this.patientData.PatNum,
                    emailTemplateID: this.selectedWiki[0].WikiPageID
                }
                console.dir(payload);
                await this.sendEmailMsg(payload);
                this.closeSlideOutPanel();
                this.setLoaded2(true);
                this.$toast.success('Email queued. It will be sent to the patient shortly.');
            } catch(err) {
                this.setErrored2(true);
                this.setMessage2(err);
            }
        },

        gotoStep2(from) {
            switch (from) {
                case 'forms':
                    if(this.selectedForms.length === 0) {
                        this.setErrored2(true);
                        this.setMessage2("Please select at least one form before continuing");
                    } else {
                        this.currentStep='formStep2FromForms';
                        let siteInfo = this.getSite(this.siteID);
                        if(siteInfo) {
                            // Prepare SMS message
                            this.smsMessage = `${siteInfo.Name}: Hello ${this.patientData.FName}, Please fill out the following forms. \n\n`;
                            for(const form of this.selectedForms) {
                                // Packet rows are flagged via `_isPacket: true` and carry `PacketID`.
                                // They resolve via /get-packet-url on the backend through processLink().
                                const templateVar = form._isPacket
                                    ? `{{patient_link_for_packet_${form.PacketID}}}`
                                    : `{{patient_link_for_form_${form.FormIDForWeb}}}`;
                                this.smsMessage = this.smsMessage + '<a href="' + templateVar + '">' + form.Name + '</a>\n';
                            }

                            // Prepare Email message — each form is a [Form: …] placeholder that becomes
                            // a clickable link (the form NAME is the link) when sendCustomEmail maps it
                            // through the shared builder. Same surrounding copy as before; SMS is unchanged.
                            const entries = formLinkPlaceholders(this.selectedForms);
                            this.sendFormEmailLinks = entries;
                            this.emailMessage = `Hello ${this.patientData.FName},\n\nPlease fill out the following forms:\n\n`
                                + entries.map(e => '- ' + e.placeholder).join('\n')
                                + '\n\nThank you,\n' + siteInfo.Name;
                        } else {
                            this.setErrored2(true);
                            this.setMessage2('Site Info not found. Please reload the page and try again');
                        }
                    }
                    break;

                case 'wiki':
                    if(this.selectedWiki.length === 0 || this.selectedWiki.length > 1) {
                        this.setErrored2(true);
                        this.setMessage2("Please select ONLY one wiki before continuing");
                    } else {
                        this.currentStep='formStep2FromWiki';
                    }
                    break;

                default:
                    break;
            }

        },

        async saveAsapList() {
            let payload = {
                siteID: this.siteID,
                patNum: this.patientData.PatNum,
                asapDays: this.asapDays,
                asapNotes: this.asapNotes
            }

            console.log('asap payload', payload);

            try {
                await this.saveAsap(payload);
                this.closeSlideOutPanel();
            } catch(err) {
                throw err;
            }
        },

        async closeSlideOutPanel() {
            let returnResults = {
                test : 'test'
            }
            this.$emit('closePanel',returnResults);
        },

        updateSelectedForms(row) {
            let idx = this.selectedForms.findIndex(each => {
                return each.FormIDForWeb === row.FormIDForWeb;
            });

            if(idx === -1)
                this.selectedForms.push(row);
            else
                this.selectedForms.splice(idx, 1);
        },

        updateSelectedWiki(row) {
            const isCurrentlySelected = this.selectedWiki.some(
                each => each.WikiPageID === row.WikiPageID
            );

            if (isCurrentlySelected) {
                // Deselecting — clear selection and restore greeting
                this.selectedWiki = [];
                this.selectedTemplateHasFormLinks = false;
                this.templateFormLinks = [];
                this.templateLinkMap = {};
                this.templateTextModified = false;
                this.smsMessage = this.smsGreeting;
            } else {
                // Selecting — enforce single selection, prefill textarea
                this.selectedWiki = [row];
                this.templateTextModified = false;
                this.smsMessage = this.parseEditorJsContent(row.Content);
            }
        },

        onSmsTextareaInput() {
            if (this.selectedWiki.length > 0) {
                this.templateTextModified = true;
            }
        },

        // Shared SMS guidance (<SmsGuidance>): block Send when the composed text is over
        // the carrier limit. Mirrors the chat composers so every SMS surface behaves the same.
        onSmsGuidance(g) {
            this.smsBlocked = !!(g && g.blocked);
            this.lastGuidance = g || null;
        },

        onWikiRowClick(event) {
            this.updateSelectedWiki(event.row);
        },

        parseEditorJsContent(content) {
            if (!content) return '';
            let linkMap = {};

            /**
             * Convert <a> tags to visible [Form: text] or [Link: text] placeholders.
             * Form links have href containing "patient_link_for_form".
             * Builds linkMap for restoring placeholders to HTML anchors before sending.
             */
            const convertLinksToPlaceholders = (html) => {
                return html.replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (match, href, displayText) => {
                    if (!displayText || displayText.trim() === '') return '';
                    const cleanText = displayText.replace(/<[^>]*>/g, '').trim();
                    let placeholder;
                    if (href.indexOf('patient_link_for_form') >= 0) {
                        placeholder = `[Form: ${cleanText}]`;
                    } else if (href.indexOf('patient_link_for_packet') >= 0) {
                        placeholder = `[Packet: ${cleanText}]`;
                    } else {
                        placeholder = `[Link: ${cleanText}]`;
                    }
                    linkMap[placeholder] = match;
                    return placeholder;
                });
            };

            try {
                if (typeof content === 'string' && !content.startsWith('{')) {
                    return content;
                }
                const parsed = typeof content === 'string' ? JSON.parse(content) : content;
                if (!parsed.blocks || !Array.isArray(parsed.blocks)) {
                    return content;
                }
                const collectFromBlock = (block, textParts) => {
                    if (!block.data) return;
                    if (block.data.text) {
                        let text = convertLinksToPlaceholders(block.data.text);
                        text = text
                            .replace(/<[^>]*>/g, '')
                            .replace(/&nbsp;/g, ' ')
                            .replace(/&amp;/g, '&')
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>')
                            .replace(/&quot;/g, '"')
                            .trim();
                        if (text) textParts.push(text);
                    }
                    if (block.data.items && Array.isArray(block.data.items)) {
                        for (const item of block.data.items) {
                            const itemText = typeof item === 'string' ? item : item.text;
                            if (itemText) {
                                let text = convertLinksToPlaceholders(itemText);
                                text = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                                if (text) textParts.push('- ' + text);
                            }
                        }
                    }
                };

                // Headers are the template's title and are normally excluded — unless they're all
                // the template has, in which case they ARE the message. Mirrors the backend renderer
                // and src/utils/smsTemplateText.js (PROD outage 2026-08-04): ten Tulip Tree templates
                // held their entire patient-facing copy in a single header block, so a paragraphs-only
                // read showed them as empty here while the backend now sends them.
                let textParts = [];
                for (const block of parsed.blocks) {
                    if (block.type === 'header') continue;
                    collectFromBlock(block, textParts);
                }
                if (textParts.length === 0) {
                    for (const block of parsed.blocks) {
                        if (block.type !== 'header') continue;
                        collectFromBlock(block, textParts);
                    }
                }
                const hasFormLinks = Object.keys(linkMap).some(k => k.startsWith('[Form:'));
                this.selectedTemplateHasFormLinks = hasFormLinks;
                this.templateLinkMap = linkMap;
                return textParts.join('\n');
            } catch (e) {
                console.warn('Failed to parse EditorJS content:', e);
                this.selectedTemplateHasFormLinks = false;
                this.templateLinkMap = {};
                if (typeof content === 'string') {
                    return content.replace(/[{}[\]"]/g, '').substring(0, 200);
                }
                return '';
            }
        },

        isSelectedWiki(wikiPageID) {
            if(this.selectedWiki) {
                let idx = this.selectedWiki.findIndex(each => {
                    return each.WikiPageID === wikiPageID;
                });
                return idx !== -1;
            } else
                return false;
        },

        isSelectedForm(formIDForWeb) {
            if(this.selectedForms) {
                let idx = this.selectedForms.findIndex(each => {
                    return each.FormIDForWeb === formIDForWeb;
                });
                return idx !== -1;
            } else
                return false;
        },
    },

    computed: {
        // WhatsApp-only office (SMS_OUTBOUND_DISABLED): has a number, may not send SMS.
        smsOutboundOff() {
            return hasSmsNumber(this.allSettings) && isSmsOutboundDisabled(this.allSettings);
        },
        smsDisabledMessage() {
            return SMS_DISABLED_MESSAGE;
        },
        ...mapGetters('wallet', ['emailChannel']),
        // Plain SMS text of the selected wiki template, for the template step's credit counter.
        // Pure extractor (the backend renderer's twin) — parseEditorJsContent writes component
        // state, so it can't run inside a computed.
        selectedWikiSmsText() {
            return this.selectedWiki.length === 1
                ? extractSmsTextFromContent(this.selectedWiki[0].Content)
                : '';
        },
        emailGreeting() {
            const firstName = this.patientData && this.patientData.FName ? this.patientData.FName : '';
            return firstName ? `Hello ${firstName},\n\n` : '';
        },
        emailHasVariables() { return !!(this.emailMessage && this.emailMessage.indexOf('{{') >= 0); },
        // OD patient.Language: '' / 'eng' for English, 'spa' for Spanish (PatientProfile shows an ES badge for 'spa').
        patientPrefersNonEnglish() {
            const lang = String((this.patientData && this.patientData.Language) || '').trim().toLowerCase();
            return !!lang && !['en', 'eng', 'english'].includes(lang);
        },
        emailWalletOn() { return process.env.VUE_APP_EMAIL_WALLET_ENABLED === 'true'; },
        // null until the wallet doc has loaded (Layout subscribes); the builder treats null as "don't block".
        emailCreditsAvailable() {
            const ch = this.emailChannel;
            const n = ch ? Number(ch.creditsAvailable) : NaN;
            return Number.isFinite(n) ? n : null;
        },
        emailCreditsBlocked() { return this.emailWalletOn && this.emailCreditsAvailable !== null && this.emailCreditsAvailable <= 0; },
        formattedSmsNumber() {
            if (this.allSettings && this.allSettings.GENERAL_PHONE && this.allSettings.GENERAL_PHONE.PropertyValue) {
                const raw = this.allSettings.GENERAL_PHONE.PropertyValue.trim();
                const digits = raw.replace(/\D/g, '');
                if (digits.length === 11 && digits.startsWith('1')) {
                    return `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
                }
                if (digits.length === 10) {
                    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
                }
                return raw;
            }
            return '';
        },
        smsHasVariables() {
            return this.smsMessage && this.smsMessage.indexOf('{{') >= 0;
        },
        smsGreeting() {
            const siteName = this.user && this.user.SiteName ? this.user.SiteName : '';
            const firstName = this.patientData && this.patientData.FName ? this.patientData.FName : '';
            if (siteName && firstName) return `${siteName}: Hello ${firstName}, `;
            if (siteName) return `${siteName}: `;
            if (firstName) return `Hello ${firstName}, `;
            return '';
        },
        deliveryMethodOptions() {
            const options = [];
            
            if (this.canSendSMS) {
                options.push({ text: 'Send via SMS', value: 'sms' });
            }
            
            if (this.canSendEmail) {
                options.push({ text: 'Send via Email', value: 'email' });
            }
            
            return options;
        }
    },

    watch: {
        // Automatically set delivery method to the only available option
        deliveryMethodOptions: {
            immediate: true,
            handler(newOptions) {
                if (newOptions.length === 1) {
                    this.deliveryMethod = newOptions[0].value;
                } else if (newOptions.length === 0) {
                    this.deliveryMethod = null;
                }
            }
        }
    }
}

</script>

<style scoped>
.sms-template-table /deep/ tbody tr {
    cursor: pointer;
}
.sms-template-table /deep/ tbody tr:hover {
    background-color: #f0fdf4;
}

/* PACKET badge shown next to packet names in the Send Form picker */
.packet-badge {
    display: inline-block;
    background-color: #6366f1;
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 3px;
    margin-right: 6px;
    letter-spacing: 0.5px;
    vertical-align: middle;
    text-transform: uppercase;
}

/* Emoji inline grid inside dropdown */
.emoji-inline-grid {
    max-height: 200px;
    overflow-y: auto;
    width: 240px;
}
.emoji-category-title {
    font-size: 0.72rem;
    text-transform: uppercase;
    color: #9ca3af;
    margin: 8px 0 4px;
    font-weight: 600;
}
.emoji-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
}
.emoji-item {
    cursor: pointer;
    font-size: 1.2rem;
    padding: 2px 4px;
    border-radius: 4px;
    line-height: 1.4;
}
.emoji-item:hover {
    background-color: #f3f4f6;
}

/* Patient Email compose panel */
.email-hint { margin-top: 8px; font-size: 0.82rem; color: #6b7280; font-style: italic; }
.email-note { margin-top: 8px; font-size: 0.88rem; border-radius: 6px; padding: 8px 12px; line-height: 1.5; }
.email-note--links { color: #15803d; background: #f0fdf4; border: 1px solid #bbf7d0; }
.email-note--edited { color: #b45309; background: #fffbeb; border: 1px solid #fde68a; }
.email-note--designed { color: #3730a3; background: #eef2ff; border: 1px solid #c7d2fe; }
.kind-badge { display: inline-block; font-size: 0.65rem; font-weight: 700; padding: 1px 6px; border-radius: 3px; letter-spacing: 0.5px; text-transform: uppercase; color: #fff; }
.kind-badge--email { background-color: #6f74e0; }
.kind-badge--text { background-color: #16a34a; }
</style>
